import { logger } from '@/utils/logger.js';
import { useTokchanStore } from '@/store/tokchanStore.js';
/**
 * 🎭 Transition Service
 * トランジション効果の管理に特化したサービス
 *
 * 責任:
 * - トランジションの開始・終了
 * - ピークタイミングの検出
 * - 既存TransitionManagerとの連携（オプション）
 * - CSS フォールバック機能
 *
 * Note: TransitionManagerがない場合でも基本機能を提供
 */

// ============================================================================
// Type Definitions
// ============================================================================

/**
 * トランジション設定
 */
export interface TransitionConfig {
  /** トランジションタイプ */
  type: TransitionType;
  /** トランジション期間（ミリ秒） */
  duration: number;
  /** イージング関数 */
  easing: EasingFunction;
  /** 有効化するエフェクト */
  effects?: TransitionEffect[];
}

/**
 * トランジションタイプ
 */
export type TransitionType =
  | 'background-switch'
  | 'bg-to-gift'
  | 'gift-to-bg'
  | 'fade'
  | 'instant'
  | 'switch-random-overlap'
  | 'switch-to-gift'
  | 'switch-from-gift'
  | 'fade-overlap'
  | 'instant-switch';

/**
 * イージング関数タイプ
 */
export type EasingFunction =
  | 'linear'
  | 'ease'
  | 'ease-in'
  | 'ease-out'
  | 'ease-in-out'
  | 'cubic-bezier';

/**
 * トランジションエフェクト
 */
export interface TransitionEffect {
  /** エフェクト名 */
  name: string;
  /** エフェクトの強度（0-1） */
  intensity: number;
  /** エフェクトの期間（ミリ秒） */
  duration: number;
}

/**
 * トランジションオプション
 */
export interface TransitionOptions {
  /** 遷移元の要素 */
  from?: HTMLElement | null;
  /** 遷移先の要素 */
  to?: HTMLElement | null;
  /** ピーク時のコールバック */
  onPeak?: () => void;
  /** 完了時のコールバック */
  onComplete?: () => void;
  /** エラー時のコールバック */
  onError?: (error: Error) => void;
}

/**
 * トランジション実行結果
 */
export interface TransitionResult {
  /** 実行成功フラグ */
  success: boolean;
  /** 実行時間（ミリ秒） */
  duration: number;
  /** エラー情報 */
  error?: Error;
  /** トランジションタイプ */
  type: TransitionType;
}

/**
 * サービス初期化オプション
 */
export interface ServiceOptions {
  /** デフォルトのトランジション期間（ミリ秒） */
  defaultDuration?: number;
  /** エフェクトの有効化 */
  enableEffects?: boolean;
  /** デバッグモード */
  debug?: boolean;
}

/**
 * トランジション状態
 */
export interface TransitionStatus {
  /** トランジション実行中フラグ */
  isTransitioning: boolean;
  /** 現在のトランジションタイプ */
  currentType: TransitionType | null;
  /** TransitionManagerの有無 */
  hasTransitionManager: boolean;
  /** 進行度（0-1） */
  progress?: number;
  /** 残り時間（ミリ秒） */
  remainingTime?: number;
}

/**
 * TransitionManagerインターフェース
 * window.d.tsのtransitionManager型と同期
 */
export interface ITransitionManager {
  /** アクティブ状態 */
  isActive?: boolean | (() => boolean);
  /** 再生中の動画 */
  playingVideo?: HTMLVideoElement | null;
  /** イベントリスナー登録 */
  on?: (event: string, callback: (...args: unknown[]) => void) => void;
  /** 透過処理適用 */
  applyTransparencyToVideo?: (video: HTMLVideoElement, container?: HTMLElement) => HTMLVideoElement | null;
  /** トランジション再生（オーバーラップ） */
  playTransitionOverlap?: (
    type: string,
    onPeak: () => void,
    onComplete: () => void
  ) => Promise<void>;
  /** 緊急クリーンアップ */
  emergencyCleanup?: () => void;
  /** その他のプロパティ */
  [key: string]: unknown;
}

/**
 * Window拡張インターフェース
 */
declare global {
  interface Window {
    /** TransitionServiceグローバルインスタンス */
    TransitionService?: typeof TransitionService;
  }
}

// ============================================================================
// TransitionService Class
// ============================================================================

/**
 * トランジションサービスクラス
 */
class TransitionService {
  // Private properties
  private transitionManager: ITransitionManager | null;
  private isTransitioning: boolean;
  private currentTransitionType: TransitionType | null;
  private peakPromise: Promise<void> | null;
  private peakResolver: (() => void) | null;
  private options: ServiceOptions;
  private startTime: number | null;

  /**
   * コンストラクタ
   */
  constructor(options: Partial<ServiceOptions> = {}) {
    this.transitionManager = null;
    this.isTransitioning = false;
    this.currentTransitionType = null;
    this.peakPromise = null;
    this.peakResolver = null;
    this.startTime = null;
    this.options = {
      defaultDuration: 300,
      enableEffects: true,
      debug: false,
      ...options,
    };
  }

  /**
   * 初期化
   */
  public initialize(): boolean {
    const debugEnabled = useTokchanStore.getState().debugSystem.enabled;
    if (this.options.debug || debugEnabled) {
      // Debug mode initialized
    }

    // 既存のTransitionManagerを利用（Zustand store経由）
    const storeTransitionManager = useTokchanStore.getState().getTransitionManager();
    if (storeTransitionManager) {
      this.transitionManager = storeTransitionManager as unknown as ITransitionManager;
      this.setupPeakDetection();
      return true;
    } else {
      // TransitionManagerなしでも動作するようにする（情報レベルのログ）
      if (this.options.debug || debugEnabled) {
        logger.info('ℹ️ TransitionManager not available - using built-in CSS transitions');
      }
      this.transitionManager = null;
      return true; // falseではなくtrueを返して初期化を続行
    }
  }

  /**
   * ピーク検出のセットアップ
   */
  private setupPeakDetection(): void {
    // TransitionManagerのピークイベントをリッスン
    if (this.transitionManager?.on) {
      this.transitionManager.on('peak', () => {
        if (this.peakResolver) {
          this.peakResolver();
          this.peakResolver = null;
        }
      });
    }
  }

  /**
   * トランジション開始
   */
  public async startTransition(
    transitionType: TransitionType,
    options: TransitionOptions = {}
  ): Promise<void> {
    if (this.isTransitioning) {
      throw new Error('Transition already in progress');
    }

    this.isTransitioning = true;
    this.currentTransitionType = transitionType;
    this.startTime = Date.now();

    // ピーク検出用のPromiseを作成
    this.peakPromise = new Promise<void>((resolve) => {
      this.peakResolver = resolve;
    });

    try {
      // TransitionManagerを使用してトランジション開始
      if (this.transitionManager?.playTransitionOverlap) {
        await this.transitionManager.playTransitionOverlap(
          this.mapTransitionType(transitionType),
          () => {
            // ピークコールバック
            if (this.peakResolver) {
              this.peakResolver();
            }
            if (options.onPeak) {
              options.onPeak();
            }
          },
          () => {
            // 完了コールバック
            this.isTransitioning = false;
            if (options.onComplete) {
              options.onComplete();
            }
          }
        );
      } else {
        // フォールバック: CSSトランジションを使用
        await this.startCSSTransition(transitionType, options);
      }
    } catch (error) {
      this.isTransitioning = false;
      if (options.onError && error instanceof Error) {
        options.onError(error);
      }
      throw error;
    }
  }

  /**
   * ピークまで待機
   */
  public async waitForPeak(): Promise<void> {
    if (!this.peakPromise) {
      throw new Error('No transition in progress');
    }

    // ピークまで待機（タイムアウト付き）
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => reject(new Error('Peak detection timeout')), 5000);
    });

    try {
      await Promise.race([this.peakPromise, timeoutPromise]);
    } catch (error) {
      logger.error('Peak detection failed:', error);
      // エラーでも処理を続行
    }
  }

  /**
   * トランジション終了
   */
  public async endTransition(): Promise<void> {
    if (!this.isTransitioning) {
      // 既に終了している場合は何もしない
      return;
    }

    // TransitionManagerが自動的に終了を管理
    // ここでは状態のクリーンアップのみ
    this.isTransitioning = false;
    this.currentTransitionType = null;
    this.startTime = null;

    // Promiseの解決を待つ
    if (this.peakResolver) {
      this.peakResolver();
    }

    this.peakPromise = null;
    this.peakResolver = null;

    // デバッグ情報
    const debugEnabled = useTokchanStore.getState().debugSystem.enabled;
    if (this.options.debug || debugEnabled) {
      // Transition ended debug log
    }
  }

  /**
   * 緊急リセット - 行き詰まったトランジション状態をクリア
   */
  public forceReset(): void {
    logger.warn('🚨 TransitionService: Force resetting transition state');
    this.isTransitioning = false;
    this.currentTransitionType = null;
    this.startTime = null;

    if (this.peakResolver) {
      this.peakResolver();
    }

    this.peakPromise = null;
    this.peakResolver = null;
  }

  /**
   * トランジションタイプのマッピング
   */
  private mapTransitionType(type: TransitionType): string {
    const typeMap: Record<TransitionType, string> = {
      'background-switch': 'switch-random-overlap',
      'bg-to-gift': 'switch-to-gift',
      'gift-to-bg': 'switch-from-gift',
      'fade': 'fade-overlap',
      'instant': 'instant-switch',
      'switch-random-overlap': 'switch-random-overlap',
      'switch-to-gift': 'switch-to-gift',
      'switch-from-gift': 'switch-from-gift',
      'fade-overlap': 'fade-overlap',
      'instant-switch': 'instant-switch',
    };

    return typeMap[type] || 'switch-random-overlap';
  }

  /**
   * CSSトランジション（フォールバック）
   */
  private async startCSSTransition(
    _transitionType: TransitionType,
    options: TransitionOptions = {}
  ): Promise<void> {
    const overlay = this.getOrCreateOverlay();

    // フェードイン
    overlay.style.opacity = '0';
    overlay.style.display = 'block';

    await this.delay(50); // レンダリング待機
    overlay.style.opacity = '1';

    // ピーク（中間地点）で解決
    await this.delay(150);
    if (this.peakResolver) {
      this.peakResolver();
    }
    if (options.onPeak) {
      options.onPeak();
    }

    // フェードアウト
    await this.delay(150);
    overlay.style.opacity = '0';

    await this.delay(300);
    overlay.style.display = 'none';

    if (options.onComplete) {
      options.onComplete();
    }
  }

  /**
   * オーバーレイ要素を取得または作成
   */
  private getOrCreateOverlay(): HTMLElement {
    let overlay = document.getElementById('transition-overlay');

    if (!overlay) {
      overlay = document.createElement('div');
      overlay.id = 'transition-overlay';
      overlay.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: black;
        opacity: 0;
        transition: opacity 0.3s ease;
        z-index: 9999;
        display: none;
        pointer-events: none;
      `;
      document.body.appendChild(overlay);
    }

    return overlay;
  }

  /**
   * 遅延ヘルパー
   */
  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /**
   * 現在のトランジション状態を取得
   */
  public getStatus(): TransitionStatus {
    const status: TransitionStatus = {
      isTransitioning: this.isTransitioning,
      currentType: this.currentTransitionType,
      hasTransitionManager: !!this.transitionManager,
    };

    // 進行度と残り時間を計算
    if (this.isTransitioning && this.startTime) {
      const elapsed = Date.now() - this.startTime;
      const duration = this.options.defaultDuration || 300;
      status.progress = Math.min(elapsed / duration, 1);
      status.remainingTime = Math.max(duration - elapsed, 0);
    }

    return status;
  }

  /**
   * トランジションを強制終了
   */
  public forceEnd(): void {
    if (this.peakResolver) {
      this.peakResolver();
    }
    this.endTransition();

    // オーバーレイを非表示
    const overlay = document.getElementById('transition-overlay');
    if (overlay) {
      overlay.style.display = 'none';
    }
  }

  /**
   * トランジション実行（統合メソッド）
   */
  public async executeTransition(
    config: TransitionConfig,
    options: TransitionOptions = {}
  ): Promise<TransitionResult> {
    const startTime = Date.now();

    try {
      await this.startTransition(config.type, options);
      const duration = Date.now() - startTime;

      return {
        success: true,
        duration,
        type: config.type,
      };
    } catch (error) {
      const duration = Date.now() - startTime;

      return {
        success: false,
        duration,
        error: error instanceof Error ? error : new Error(String(error)),
        type: config.type,
      };
    }
  }
}

// ============================================================================
// Export
// ============================================================================

// CommonJS export
// ES6 Export
export { TransitionService };

if (typeof module !== 'undefined' && module.exports) {
  module.exports = TransitionService;
}

// ES6 export
export default TransitionService;
